import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, Loader2, Check, Tag, ArrowLeft, Mail, Phone, Truck as TruckIcon, UserSquare2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useDriverAuth } from "../context/DriverAuthContext";
import api from "../utils/api";
import toast from "react-hot-toast";

/* ─── CSS animations injected once ─────────────────────────────── */
const CSS = `
@media (max-width: 420px) {
  .lk-otp-box { width: 38px !important; height: 50px !important; font-size: 20px !important; }
  .lk-otp-row { gap: 6px !important; }
}
@media (max-width: 360px) {
  .lk-otp-box { width: 33px !important; height: 46px !important; font-size: 17px !important; }
  .lk-otp-row { gap: 4px !important; }
}
@keyframes authTruckMove {
  0%   { transform: translateX(-10px); }
  50%  { transform: translateX(10px); }
  100% { transform: translateX(-10px); }
}
@keyframes authWheelSpin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes pinPulse {
  0%,100% { transform: scale(1); opacity:1; }
  50%     { transform: scale(1.25); opacity:0.7; }
}
@keyframes routeDraw {
  from { stroke-dashoffset: 400; }
  to   { stroke-dashoffset: 0; }
}
@keyframes cardFloat1 {
  0%,100% { transform: translateY(0px) rotate(-2deg); }
  50%     { transform: translateY(-8px) rotate(-2deg); }
}
@keyframes cardFloat2 {
  0%,100% { transform: translateY(0px) rotate(2deg); }
  50%     { transform: translateY(-6px) rotate(2deg); }
}
@keyframes cardFloat3 {
  0%,100% { transform: translateY(0px) rotate(-1deg); }
  50%     { transform: translateY(-10px) rotate(-1deg); }
}
@keyframes roadMove {
  from { stroke-dashoffset: 0; }
  to   { stroke-dashoffset: -60; }
}
@keyframes spinLoader {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes glowPulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); }
  50%     { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
}
`;

/* ─── Left panel illustration ───────────────────────────────────── */
function Illustration() {
  return (
    <div style={{ position:"relative", width:"100%", height:"100%", overflow:"hidden" }}>
      <svg
        viewBox="0 0 420 520"
        style={{ width:"100%", height:"100%", position:"absolute", inset:0 }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Sky gradient */}
        <defs>
          <linearGradient id="ilSky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#0F172A" />
            <stop offset="100%" stopColor="#1E1B4B" />
          </linearGradient>
          <linearGradient id="ilRoad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#374151" />
            <stop offset="100%" stopColor="#1F2937" />
          </linearGradient>
          <linearGradient id="ilGround" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#14532D" />
            <stop offset="100%" stopColor="#0F2017" />
          </linearGradient>
        </defs>
        <rect width="420" height="520" fill="url(#ilSky)" />

        {/* Stars */}
        {[[30,40],[80,20],[140,60],[200,30],[270,50],[340,25],[390,70],[60,90],[310,80],[170,15]].map(([x,y],i) => (
          <circle key={i} cx={x} cy={y} r={i%3===0?1.2:0.7} fill="white" opacity={0.5+i*0.04} />
        ))}

        {/* Ground */}
        <rect x="0" y="380" width="420" height="140" fill="url(#ilGround)" />

        {/* Road */}
        <rect x="0" y="370" width="420" height="40" fill="url(#ilRoad)" />
        <line x1="0" y1="390" x2="420" y2="390" stroke="#FCD34D" strokeWidth="2.5" strokeDasharray="30,20"
          style={{ animation:"roadMove 1.5s linear infinite" }} />

        {/* GPS Route path */}
        <path d="M60,340 Q120,280 180,320 Q240,360 300,280 Q340,220 370,260"
          stroke="#6366F1" strokeWidth="3" fill="none"
          strokeDasharray="400" strokeDashoffset="400"
          style={{ animation:"routeDraw 3s ease forwards 0.5s" }}
          strokeLinecap="round" />

        {/* Location pins along route */}
        {[[60,340],[180,320],[300,280],[370,260]].map(([x,y],i) => (
          <g key={i} style={{ animation:`pinPulse 1.8s ${i*0.4}s ease-in-out infinite` }}>
            <circle cx={x} cy={y} r="7" fill={i===0?"#10B981":i===3?"#F59E0B":"#6366F1"} opacity="0.9" />
            <circle cx={x} cy={y} r="4" fill="white" />
            <circle cx={x} cy={y} r="2" fill={i===0?"#10B981":i===3?"#F59E0B":"#6366F1"} />
          </g>
        ))}

        {/* Truck on road */}
        <g style={{ animation:"authTruckMove 3s ease-in-out infinite" }}>
          {/* Trailer */}
          <rect x="80" y="348" width="120" height="30" rx="3" fill="#4F46E5" />
          <rect x="82" y="350" width="116" height="26" rx="2" fill="#6366F1" />
          <text x="142" y="367" textAnchor="middle" fontSize="5.5" fontWeight="800"
            fill="white" fontFamily="Arial" letterSpacing="0.5">LORRYKHOJO</text>
          {/* Cab */}
          <rect x="200" y="352" width="38" height="26" rx="3" fill="#3730A3" />
          <path d="M202,352 Q204,342 212,340 L232,340 L236,352 Z" fill="#312E81" />
          <path d="M204,350 Q206,342 212,341 L225,341 L228,350 Z" fill="#818CF8" opacity="0.6" />
          <rect x="233" y="355" width="5" height="4" rx="1" fill="#FEF3C7" />
          {/* Wheels */}
          <g style={{ transformOrigin:"100px 379px", animation:"authWheelSpin 0.7s linear infinite" }}>
            <circle cx="100" cy="379" r="8" fill="#1E1B4B" />
            <circle cx="100" cy="379" r="5" fill="#374151" />
            <circle cx="100" cy="379" r="2" fill="#6366F1" />
          </g>
          <g style={{ transformOrigin:"136px 379px", animation:"authWheelSpin 0.7s linear infinite" }}>
            <circle cx="136" cy="379" r="8" fill="#1E1B4B" />
            <circle cx="136" cy="379" r="5" fill="#374151" />
            <circle cx="136" cy="379" r="2" fill="#6366F1" />
          </g>
          <g style={{ transformOrigin:"215px 379px", animation:"authWheelSpin 0.7s linear infinite" }}>
            <circle cx="215" cy="379" r="8" fill="#1E1B4B" />
            <circle cx="215" cy="379" r="5" fill="#374151" />
            <circle cx="215" cy="379" r="2" fill="#6366F1" />
          </g>
        </g>
      </svg>

      {/* Floating analytics cards */}
      <div style={{
        position:"absolute", top:"8%", left:"8%", right:"8%",
        pointerEvents:"none",
      }}>
        {/* Card 1 — Speed */}
        <div style={{
          position:"absolute", top:10, left:10,
          background:"rgba(255,255,255,0.08)",
          backdropFilter:"blur(12px)",
          border:"1px solid rgba(255,255,255,0.15)",
          borderRadius:12, padding:"10px 14px",
          animation:"cardFloat1 3.5s ease-in-out infinite",
          minWidth:130,
        }}>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.5)", fontWeight:600, letterSpacing:1, textTransform:"uppercase" }}>Speed</div>
          <div style={{ fontSize:20, fontWeight:800, color:"#10B981", marginTop:2 }}>72 km/h</div>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.4)", marginTop:2 }}>↑ on NH-48</div>
        </div>

        {/* Card 2 — Toll crossed */}
        <div style={{
          position:"absolute", top:10, right:0,
          background:"rgba(255,255,255,0.08)",
          backdropFilter:"blur(12px)",
          border:"1px solid rgba(255,255,255,0.15)",
          borderRadius:12, padding:"10px 14px",
          animation:"cardFloat2 4s ease-in-out infinite",
          minWidth:130,
        }}>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.5)", fontWeight:600, letterSpacing:1, textTransform:"uppercase" }}>Last Toll</div>
          <div style={{ fontSize:13, fontWeight:700, color:"#F59E0B", marginTop:2 }}>Delhi Toll Plaza</div>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.4)", marginTop:2 }}>2 mins ago · ₹85</div>
        </div>

        {/* Card 3 — Fleet status */}
        <div style={{
          position:"absolute", top:90, left:"20%",
          background:"rgba(99,102,241,0.2)",
          backdropFilter:"blur(12px)",
          border:"1px solid rgba(99,102,241,0.35)",
          borderRadius:12, padding:"10px 14px",
          animation:"cardFloat3 4.5s ease-in-out infinite",
          minWidth:140,
        }}>
          <div style={{ fontSize:9, color:"rgba(255,255,255,0.5)", fontWeight:600, letterSpacing:1, textTransform:"uppercase" }}>Fleet Active</div>
          <div style={{ fontSize:20, fontWeight:800, color:"#fff", marginTop:2 }}>8 / 10</div>
          <div style={{ display:"flex", gap:3, marginTop:4 }}>
            {[...Array(10)].map((_,i) => (
              <div key={i} style={{ width:6, height:6, borderRadius:2,
                background: i < 8 ? "#10B981" : "rgba(255,255,255,0.2)" }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Input field ────────────────────────────────────────────────── */
function Field({ label, type="text", value, onChange, placeholder, error, required }) {
  const [show, setShow]       = useState(false);
  const [focused, setFocused] = useState(false);
  const isPwd = type === "password";
  return (
    <div>
      <label style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.7)", display:"block", marginBottom:5, letterSpacing:0.3 }}>
        {label}{required && <span style={{ color:"#F87171", marginLeft:2 }}>*</span>}
      </label>
      <div style={{ position:"relative" }}>
        <input
          type={isPwd && show ? "text" : type}
          value={value} onChange={onChange} placeholder={placeholder}
          style={{
            width:"100%", boxSizing:"border-box", height:44,
            paddingLeft:14, paddingRight: isPwd ? 42 : 14,
            background:"rgba(255,255,255,0.06)",
            border:`1.5px solid ${error ? "#F87171" : focused ? "#818CF8" : "rgba(255,255,255,0.12)"}`,
            borderRadius:10, fontSize:14, color:"#fff", outline:"none",
            transition:"border-color 0.2s, box-shadow 0.2s",
            boxShadow: focused ? "0 0 0 3px rgba(129,140,248,0.18)" : "none",
          }}
          onFocus={() => setFocused(true)}
          onBlur={()  => setFocused(false)}
        />
        {isPwd && (
          <button type="button" onClick={() => setShow(s => !s)} style={{
            position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
            background:"none", border:"none", cursor:"pointer",
            color:"rgba(255,255,255,0.4)", display:"flex",
          }}>
            {show ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
      {error && <p style={{ fontSize:11, color:"#F87171", marginTop:4, margin:"4px 0 0" }}>{error}</p>}
    </div>
  );
}

/* ─── Main AuthPage ─────────────────────────────────────────────── */
export default function AuthPage({ forcedTier } = {}) {
  const { login, register, session } = useAuth();
  const { driverLogin, driver } = useDriverAuth();
  const navigate = useNavigate();
  const tier = forcedTier === "fleet" ? "fleet" : forcedTier === "tracking" ? "tracking" : null;

  const [tab,          setTab]          = useState(tier ? "register" : "login");
  const [loading,      setLoading]      = useState(false);
  const [globalError,  setGlobalError]  = useState("");
  const [loginForm,    setLoginForm]    = useState({ email:"", password:"" });
  const [loginErrors,  setLoginErrors]  = useState({});
  // Login-side product/actor choice — "Vehicle Tracking" + "Fleet Owner" are the
  // defaults, so an existing owner who never touches these sees today's exact
  // login form with zero behavior change.
  const [loginProduct, setLoginProduct] = useState("tracking"); // 'tracking' | 'fleet'
  const [loginActor,   setLoginActor]   = useState("owner");    // 'owner' | 'driver'
  const [driverForm,   setDriverForm]   = useState({ email:"", password:"", remember:true });
  const [driverErrors, setDriverErrors] = useState({});
  const [regForm,      setRegForm]      = useState({ name:"", email:"", password:"", confirm:"", phone:"", coupon:"", gst:"", pan:"", address:"" });
  const [selectedTier, setSelectedTier] = useState(tier || "tracking");
  const [regErrors,    setRegErrors]    = useState({});
  const [narrow,       setNarrow]       = useState(window.innerWidth < 860);
  // Forgot-password state (3-step OTP flow)
  const [forgotMode,    setForgotMode]    = useState(false);
  const [forgotStep,    setForgotStep]    = useState(1);          // 1=email 2=otp 3=newpass 4=done
  const [forgotEmail,   setForgotEmail]   = useState("");
  const [forgotOtp,     setForgotOtp]     = useState(["","","","","",""]);
  const [forgotPass,    setForgotPass]    = useState("");
  const [forgotConfirm, setForgotConfirm] = useState("");
  const [showForgotPass,setShowForgotPass]= useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError,   setForgotError]   = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMsg,      setResendMsg]      = useState("");

  useEffect(() => {
    if (session) navigate("/app", { replace:true });
  }, [session, navigate]);

  useEffect(() => {
    if (driver) navigate("/driver-app", { replace:true });
  }, [driver, navigate]);

  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < 860);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  function setL(k, v) { setLoginForm(p => ({...p,[k]:v})); setLoginErrors(p => ({...p,[k]:""})); setGlobalError(""); }
  function setR(k, v) { setRegForm(p => ({...p,[k]:v}));   setRegErrors(p => ({...p,[k]:""}));   setGlobalError(""); }
  function setD(k, v) { setDriverForm(p => ({...p,[k]:v})); setDriverErrors(p => ({...p,[k]:""})); setGlobalError(""); }

  async function handleDriverLogin(e) {
    e.preventDefault();
    const errs = {};
    if (!driverForm.email)    errs.email    = "Email is required";
    if (!driverForm.password) errs.password = "Password is required";
    if (Object.keys(errs).length) { setDriverErrors(errs); return; }
    setLoading(true);
    try {
      await driverLogin(driverForm.email, driverForm.password);
      navigate("/driver-app");
    }
    catch (err) { setGlobalError(err.response?.data?.error || "Login failed"); }
    finally { setLoading(false); }
  }

  async function handleLogin(e) {
    e.preventDefault();
    const email    = loginForm.email.trim();
    const password = loginForm.password.trim();
    const errs = {};
    if (!email)    errs.email    = "Email is required";
    if (!password) errs.password = "Password is required";
    if (Object.keys(errs).length) { setLoginErrors(errs); return; }
    setLoading(true);
    try { await login(email, password); navigate("/app"); }
    catch (err) { setGlobalError(err.message || "Login failed"); }
    finally { setLoading(false); }
  }

  async function handleRegister(e) {
    e.preventDefault();
    const email    = regForm.email.trim();
    const password = regForm.password.trim();
    const confirm  = regForm.confirm.trim();
    const errs = {};
    if (!regForm.name.trim())  errs.name     = "Organization name is required";
    if (!email)                errs.email    = "Email is required";
    if (!regForm.phone.trim()) errs.phone    = "Phone number is required";
    else if (!/^[6-9]\d{9}$/.test(regForm.phone.replace(/\s+/g, ""))) errs.phone = "Enter a valid 10-digit Indian mobile number";
    if (!password)             errs.password = "Password is required";
    else if (password.length < 6) errs.password = "Password must be at least 6 characters";
    if (!confirm)              errs.confirm  = "Please confirm your password";
    else if (password !== confirm) errs.confirm = "Passwords do not match";
    if (regForm.gst.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(regForm.gst.trim().toUpperCase()))
      errs.gst = "Enter a valid 15-character GSTIN";
    if (regForm.pan.trim() && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(regForm.pan.trim().toUpperCase()))
      errs.pan = "Enter a valid 10-character PAN";
    if (Object.keys(errs).length) { setRegErrors(errs); return; }
    setLoading(true);
    try {
      const couponApplied = await register(
        regForm.name, email, password, regForm.coupon, regForm.phone,
        regForm.gst, regForm.pan, regForm.address, selectedTier,
      );
      if (couponApplied) toast?.success?.("Coupon applied — full access activated!");
      navigate("/app");
    }
    catch (err) { setGlobalError(err.message || "Registration failed"); }
    finally { setLoading(false); }
  }

  function openForgot() {
    setForgotMode(true);
    setForgotEmail(loginForm.email); // pre-fill with whatever they typed

    setForgotError("");
    setGlobalError("");
  }

  function closeForgot() {
    setForgotMode(false);
    setForgotStep(1);
    setForgotEmail("");
    setForgotOtp(["","","","","",""]);
    setForgotPass("");
    setForgotConfirm("");
    setForgotError("");
  }

  // Step 1: send OTP
  async function handleSendOtp(e) {
    if (e?.preventDefault) e.preventDefault();
    if (!forgotEmail.trim()) { setForgotError("Please enter your email address."); return; }
    setForgotLoading(true); setForgotError("");
    try {
      await api.post("/api/v/recover", { email: forgotEmail.trim() });
      setForgotStep(2);
    } catch (err) {
      setForgotError(err.message || "Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  }

  // Resend OTP — clears boxes, shows feedback, starts 60s cooldown
  async function handleResendOtp() {
    if (resendCooldown > 0) return;
    setForgotOtp(["","","","","",""]);
    setForgotError(""); setResendMsg("");
    setForgotLoading(true);
    try {
      await api.post("/api/v/recover", { email: forgotEmail.trim() });
      setResendMsg("OTP sent! Check your inbox.");
      setResendCooldown(60);
      const timer = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
      setTimeout(() => document.getElementById("otp-0")?.focus(), 100);
    } catch {
      setForgotError("Could not resend. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  }

  // OTP box input helpers
  function handleOtpChange(idx, val) {
    const v = val.replace(/\D/g, "").slice(0, 1);
    const next = [...forgotOtp]; next[idx] = v; setForgotOtp(next); setForgotError("");
    if (v && idx < 5) document.getElementById(`otp-${idx + 1}`)?.focus();
  }
  function handleOtpKeyDown(idx, e) {
    if (e.key === "Backspace" && !forgotOtp[idx] && idx > 0)
      document.getElementById(`otp-${idx - 1}`)?.focus();
  }
  function handleOtpPaste(e) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setForgotOtp(pasted.split("")); e.preventDefault();
      document.getElementById("otp-5")?.focus();
    }
  }

  // Step 2: verify OTP
  async function handleVerifyOtp(e) {
    e.preventDefault();
    const code = forgotOtp.join("");
    if (code.length < 6) { setForgotError("Enter the full 6-digit code."); return; }
    setForgotLoading(true); setForgotError("");
    try {
      await api.post("/api/v/verify-otp", { email: forgotEmail.trim(), otp: code });
      setForgotStep(3);
    } catch (err) {
      setForgotError(err.message || "Invalid or expired OTP.");
    } finally {
      setForgotLoading(false);
    }
  }

  // Step 3: set new password
  async function handleResetPassword(e) {
    e.preventDefault();
    if (!forgotPass)             { setForgotError("Enter a new password."); return; }
    if (forgotPass.length < 6)   { setForgotError("Password must be at least 6 characters."); return; }
    if (forgotPass !== forgotConfirm) { setForgotError("Passwords do not match."); return; }
    setForgotLoading(true); setForgotError("");
    try {
      await api.post("/api/v/renew", { email: forgotEmail.trim(), otp: forgotOtp.join(""), password: forgotPass });
      setForgotStep(4);
      toast.success("Password updated! Please log in.");
      setTimeout(() => closeForgot(), 2500);
    } catch (err) {
      setForgotError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  }

  return (
    <>
      <style>{CSS}</style>
      <div style={{
        display:"flex", height:"100vh", overflow:"hidden",
        background:"#0F172A",
        fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      }}>

        {/* ── LEFT ILLUSTRATION PANEL ── */}
        {!narrow && (
          <motion.div
            initial={{ opacity:0, x:-30 }}
            animate={{ opacity:1, x:0 }}
            transition={{ duration:0.6 }}
            style={{ width:"45%", flexShrink:0, position:"relative", overflow:"hidden" }}
          >
            {/* Subtle purple gradient overlay */}
            <div style={{
              position:"absolute", inset:0, zIndex:1,
              background:"linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(139,92,246,0.06) 100%)",
              pointerEvents:"none",
            }} />
            <Illustration />

            {/* Brand badge bottom */}
            <div style={{
              position:"absolute", bottom:28, left:"50%", transform:"translateX(-50%)",
              zIndex:10, display:"flex", alignItems:"center", gap:10,
              background:"rgba(255,255,255,0.06)", backdropFilter:"blur(12px)",
              border:"1px solid rgba(255,255,255,0.1)",
              borderRadius:99, padding:"10px 20px",
            }}>
              <div style={{
                width:30, height:30, borderRadius:9,
                background:"linear-gradient(135deg,#6366F1,#8B5CF6)",
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                  <circle cx="8" cy="8" r="2" fill="#fff"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:"#fff", letterSpacing:1 }}>LORRYKHOJO</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,0.4)", letterSpacing:0.8, textTransform:"uppercase" }}>Fleet Intelligence</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── RIGHT AUTH PANEL ── */}
        <div style={{
          flex:1, overflowY:"auto",
          display:"flex", flexDirection:"column",
          background:"linear-gradient(135deg, #0F172A 0%, #1a1240 100%)",
        }}>
          {/* Back link */}
          <div style={{ padding: narrow ? "max(20px, calc(var(--sat, 0px) + 12px)) 20px 0" : "24px 40px 0", flexShrink:0 }}>
            <button
              onClick={() => navigate("/")}
              style={{
                display:"inline-flex", alignItems:"center", gap:6,
                background:"none", border:"none",
                fontSize:13, color:"rgba(255,255,255,0.4)", cursor:"pointer",
                transition:"color 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,0.8)"}
              onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,0.4)"}
            >
              <ArrowLeft size={14} /> Back to home
            </button>
          </div>

          {/* Centered card */}
          <div style={{
            flex:1, display:"flex", alignItems:"center", justifyContent:"center",
            padding: narrow ? "20px 16px 32px" : "32px 40px",
          }}>
            <motion.div
              initial={{ opacity:0, y:24 }}
              animate={{ opacity:1, y:0 }}
              transition={{ duration:0.5, delay:0.1 }}
              style={{
                width:"100%", maxWidth:400,
                background:"rgba(255,255,255,0.05)",
                backdropFilter:"blur(20px)",
                border:"1px solid rgba(255,255,255,0.1)",
                borderRadius:20,
                padding: "32px 32px 28px",
                boxShadow:"0 24px 60px rgba(0,0,0,0.5)",
              }}
            >
              {/* Logo (mobile only — desktop has left panel) */}
              {narrow && (
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:24 }}>
                  <div style={{
                    width:34, height:34, borderRadius:10,
                    background:"linear-gradient(135deg,#6366F1,#8B5CF6)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                      <circle cx="8" cy="8" r="2" fill="#fff"/>
                    </svg>
                  </div>
                  <span style={{ fontSize:15, fontWeight:800, color:"#fff", letterSpacing:1 }}>LORRYKHOJO</span>
                </div>
              )}

              {/* Heading */}
              <div style={{ marginBottom:24 }}>
                <h1 style={{ fontSize:22, fontWeight:800, color:"#fff", margin:"0 0 6px", letterSpacing:-0.3 }}>
                  {forgotMode
                    ? (forgotStep === 1 ? "Forgot Password" : forgotStep === 2 ? "Enter OTP" : forgotStep === 3 ? "New Password" : "Password Updated!")
                    : (tab === "login" ? "Welcome Back" : selectedTier === "fleet" ? "Get Fleet Management" : "Create Account")}
                </h1>
                <p style={{ fontSize:13, color:"rgba(255,255,255,0.45)", margin:0 }}>
                  {forgotMode
                    ? (forgotStep === 1 ? "We'll send a 6-digit OTP to your email."
                      : forgotStep === 2 ? `Code sent to ${forgotEmail}`
                      : forgotStep === 3 ? "Choose a strong password."
                      : "You can now log in with your new password.")
                    : (tab === "login" ? "Track your fleet in real time."
                      : selectedTier === "fleet" ? "Vehicle tracking plus documents, maintenance, fuel logs, and alerts."
                      : "Join LorryKhojo and start tracking.")}
                </p>
                {/* Step progress dots */}
                {forgotMode && forgotStep < 4 && (
                  <div style={{ display:"flex", gap:6, marginTop:12 }}>
                    {[1,2,3].map(s => (
                      <div key={s} style={{
                        height:3, flex:1, borderRadius:99,
                        background: s <= forgotStep ? "#6366F1" : "rgba(255,255,255,0.12)",
                        transition:"background 0.3s",
                      }} />
                    ))}
                  </div>
                )}
              </div>

              {/* Tabs — hidden in forgot mode */}
              {!forgotMode && (
                <div style={{
                  display:"flex", background:"rgba(255,255,255,0.06)",
                  borderRadius:10, padding:3, marginBottom:24,
                }}>
                  {[
                    { key:"login",    label:"Login" },
                    { key:"register", label:"Sign Up" },
                  ].map(({ key, label }) => (
                    <button key={key} onClick={() => { setTab(key); setGlobalError(""); }} style={{
                      flex:1, height:36, borderRadius:8, border:"none", cursor:"pointer",
                      fontSize:13, fontWeight:600,
                      background: tab===key ? "linear-gradient(135deg,#6366F1,#7C3AED)" : "none",
                      color: tab===key ? "#fff" : "rgba(255,255,255,0.45)",
                      transition:"all 0.2s",
                      boxShadow: tab===key ? "0 2px 10px rgba(99,102,241,0.4)" : "none",
                    }}>{label}</button>
                  ))}
                </div>
              )}

              {/* ── FORGOT PASSWORD PANEL — 3-step OTP flow ── */}
              {forgotMode && (
                <AnimatePresence mode="wait">

                  {/* Step 1: enter email */}
                  {forgotStep === 1 && (
                    <motion.form key="step1"
                      initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:16 }}
                      transition={{ duration:0.2 }}
                      onSubmit={handleSendOtp} noValidate
                      style={{ display:"flex", flexDirection:"column", gap:16 }}
                    >
                      <div style={{ position:"relative" }}>
                        <Mail size={14} style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:"rgba(255,255,255,0.3)", pointerEvents:"none" }} />
                        <input
                          type="email" autoFocus required
                          value={forgotEmail}
                          onChange={e => { setForgotEmail(e.target.value); setForgotError(""); }}
                          placeholder="Your registered email"
                          style={{
                            width:"100%", boxSizing:"border-box", height:44,
                            paddingLeft:36, paddingRight:14,
                            background:"rgba(255,255,255,0.06)",
                            border:`1.5px solid ${forgotError ? "#F87171" : "rgba(255,255,255,0.12)"}`,
                            borderRadius:10, fontSize:14, color:"#fff", outline:"none",
                          }}
                        />
                      </div>
                      {forgotError && <p style={{ fontSize:12, color:"#F87171", margin:"-8px 0 0" }}>{forgotError}</p>}
                      <button type="submit" disabled={forgotLoading} style={{
                        width:"100%", height:46, borderRadius:12, border:"none",
                        background: forgotLoading ? "rgba(99,102,241,0.5)" : "linear-gradient(135deg,#6366F1,#7C3AED)",
                        color:"#fff", fontSize:14, fontWeight:700,
                        cursor: forgotLoading ? "default" : "pointer",
                        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                        boxShadow: forgotLoading ? "none" : "0 4px 20px rgba(99,102,241,0.45)",
                        transition:"all 0.2s",
                      }}>
                        {forgotLoading ? <><Loader2 size={16} style={{ animation:"spinLoader 1s linear infinite" }} /> Sending…</> : "Send OTP →"}
                      </button>
                      <button type="button" onClick={closeForgot} style={{
                        background:"none", border:"none", color:"rgba(255,255,255,0.4)",
                        fontSize:12, cursor:"pointer", padding:0, textAlign:"center", transition:"color 0.15s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,0.8)"}
                        onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,0.4)"}
                      >← Back to Login</button>
                    </motion.form>
                  )}

                  {/* Step 2: enter OTP */}
                  {forgotStep === 2 && (
                    <motion.form key="step2"
                      initial={{ opacity:0, x:16 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-16 }}
                      transition={{ duration:0.2 }}
                      onSubmit={handleVerifyOtp} noValidate
                      style={{ display:"flex", flexDirection:"column", gap:20 }}
                    >
                      <p style={{ margin:0, fontSize:13, color:"rgba(255,255,255,0.5)", textAlign:"center" }}>
                        Check <strong style={{ color:"rgba(255,255,255,0.8)" }}>{forgotEmail}</strong> for your code
                      </p>
                      {/* 6 OTP digit boxes */}
                      <div className="lk-otp-row" style={{ display:"flex", gap:8, justifyContent:"center" }}>
                        {forgotOtp.map((digit, i) => (
                          <input
                            key={i} id={`otp-${i}`}
                            className="lk-otp-box"
                            type="text" inputMode="numeric" maxLength={1}
                            value={digit}
                            onChange={e => handleOtpChange(i, e.target.value)}
                            onKeyDown={e => handleOtpKeyDown(i, e)}
                            onPaste={i === 0 ? handleOtpPaste : undefined}
                            autoFocus={i === 0}
                            style={{
                              width:44, height:54, textAlign:"center",
                              fontSize:24, fontWeight:800, color:"#fff",
                              background:"rgba(255,255,255,0.06)",
                              border:`2px solid ${forgotError ? "#F87171" : digit ? "#6366F1" : "rgba(255,255,255,0.15)"}`,
                              borderRadius:10, outline:"none", caretColor:"#818CF8",
                              transition:"border-color 0.15s",
                            }}
                          />
                        ))}
                      </div>
                      {forgotError && <p style={{ fontSize:12, color:"#F87171", margin:"-8px 0 0", textAlign:"center" }}>{forgotError}</p>}
                      <button type="submit" disabled={forgotLoading} style={{
                        width:"100%", height:46, borderRadius:12, border:"none",
                        background: forgotLoading ? "rgba(99,102,241,0.5)" : "linear-gradient(135deg,#6366F1,#7C3AED)",
                        color:"#fff", fontSize:14, fontWeight:700,
                        cursor: forgotLoading ? "default" : "pointer",
                        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                        boxShadow: forgotLoading ? "none" : "0 4px 20px rgba(99,102,241,0.45)",
                        transition:"all 0.2s",
                      }}>
                        {forgotLoading ? <><Loader2 size={16} style={{ animation:"spinLoader 1s linear infinite" }} /> Verifying…</> : "Verify OTP →"}
                      </button>
                      {resendMsg && (
                        <p style={{ fontSize:12, color:"#6EE7B7", textAlign:"center", margin:"-4px 0 0" }}>
                          {resendMsg}
                        </p>
                      )}
                      <div style={{ display:"flex", justifyContent:"space-between" }}>
                        <button type="button" onClick={() => { setForgotStep(1); setForgotError(""); setResendMsg(""); setResendCooldown(0); }} style={{
                          background:"none", border:"none", color:"rgba(255,255,255,0.4)",
                          fontSize:12, cursor:"pointer", padding:0, transition:"color 0.15s",
                        }}
                          onMouseEnter={e => e.currentTarget.style.color="rgba(255,255,255,0.8)"}
                          onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,0.4)"}
                        >← Change email</button>
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={resendCooldown > 0 || forgotLoading}
                          style={{
                            background:"none", border:"none", padding:0, fontSize:12, cursor: resendCooldown > 0 ? "default" : "pointer",
                            color: resendCooldown > 0 ? "rgba(255,255,255,0.25)" : "rgba(99,102,241,0.7)",
                            transition:"color 0.15s",
                          }}
                          onMouseEnter={e => { if (resendCooldown === 0) e.currentTarget.style.color="#818CF8"; }}
                          onMouseLeave={e => { if (resendCooldown === 0) e.currentTarget.style.color="rgba(99,102,241,0.7)"; }}
                        >
                          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                        </button>
                      </div>
                    </motion.form>
                  )}

                  {/* Step 3: new password */}
                  {forgotStep === 3 && (
                    <motion.form key="step3"
                      initial={{ opacity:0, x:16 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-16 }}
                      transition={{ duration:0.2 }}
                      onSubmit={handleResetPassword} noValidate
                      style={{ display:"flex", flexDirection:"column", gap:14 }}
                    >
                      <div style={{ position:"relative" }}>
                        <input
                          type={showForgotPass ? "text" : "password"} autoFocus
                          value={forgotPass}
                          onChange={e => { setForgotPass(e.target.value); setForgotError(""); }}
                          placeholder="New password (min 6 chars)"
                          style={{
                            width:"100%", boxSizing:"border-box", height:44,
                            paddingLeft:14, paddingRight:42,
                            background:"rgba(255,255,255,0.06)",
                            border:`1.5px solid ${forgotError ? "#F87171" : "rgba(255,255,255,0.12)"}`,
                            borderRadius:10, fontSize:14, color:"#fff", outline:"none",
                          }}
                        />
                        <button type="button" onClick={() => setShowForgotPass(s => !s)} style={{
                          position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
                          background:"none", border:"none", cursor:"pointer",
                          color:"rgba(255,255,255,0.4)", display:"flex",
                        }}>
                          {showForgotPass ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      <input
                        type={showForgotPass ? "text" : "password"}
                        value={forgotConfirm}
                        onChange={e => { setForgotConfirm(e.target.value); setForgotError(""); }}
                        placeholder="Confirm new password"
                        style={{
                          width:"100%", boxSizing:"border-box", height:44,
                          paddingLeft:14, paddingRight:14,
                          background:"rgba(255,255,255,0.06)",
                          border:`1.5px solid ${forgotError ? "#F87171" : "rgba(255,255,255,0.12)"}`,
                          borderRadius:10, fontSize:14, color:"#fff", outline:"none",
                        }}
                      />
                      {forgotError && <p style={{ fontSize:12, color:"#F87171", margin:"-4px 0 0" }}>{forgotError}</p>}
                      <button type="submit" disabled={forgotLoading} style={{
                        width:"100%", height:46, borderRadius:12, border:"none",
                        background: forgotLoading ? "rgba(99,102,241,0.5)" : "linear-gradient(135deg,#6366F1,#7C3AED)",
                        color:"#fff", fontSize:14, fontWeight:700,
                        cursor: forgotLoading ? "default" : "pointer",
                        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                        boxShadow: forgotLoading ? "none" : "0 4px 20px rgba(99,102,241,0.45)",
                        transition:"all 0.2s",
                      }}>
                        {forgotLoading ? <><Loader2 size={16} style={{ animation:"spinLoader 1s linear infinite" }} /> Saving…</> : "Set New Password →"}
                      </button>
                    </motion.form>
                  )}

                  {/* Step 4: success */}
                  {forgotStep === 4 && (
                    <motion.div key="step4"
                      initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }}
                      style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:16, padding:"8px 0 12px" }}
                    >
                      <div style={{
                        width:60, height:60, borderRadius:"50%",
                        background:"rgba(16,185,129,0.15)",
                        border:"1.5px solid rgba(16,185,129,0.4)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <Check size={26} color="#10B981" strokeWidth={2.5} />
                      </div>
                      <div style={{ textAlign:"center" }}>
                        <p style={{ fontSize:15, fontWeight:700, color:"#fff", margin:"0 0 6px" }}>Password Updated!</p>
                        <p style={{ fontSize:12, color:"rgba(255,255,255,0.45)", margin:0 }}>
                          Redirecting you to the login page…
                        </p>
                      </div>
                    </motion.div>
                  )}

                </AnimatePresence>
              )}

              {/* Global error */}
              {!forgotMode && globalError && (
                <motion.div
                  initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }}
                  style={{
                    background:"rgba(248,113,113,0.1)", border:"1px solid rgba(248,113,113,0.3)",
                    borderRadius:8, padding:"10px 14px", marginBottom:16,
                    fontSize:13, color:"#FCA5A5",
                  }}
                >{globalError}</motion.div>
              )}

              {/* Forms */}
              {!forgotMode && <AnimatePresence mode="wait">
                {tab === "login" ? (
                  <motion.form key="login"
                    initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-16 }}
                    transition={{ duration:0.2 }}
                    onSubmit={loginProduct === "fleet" && loginActor === "driver" ? handleDriverLogin : handleLogin} noValidate
                    style={{ display:"flex", flexDirection:"column", gap:16 }}
                  >
                    {/* Product choice — "Vehicle Tracking" stays selected by default, so an
                        existing owner who never touches this sees today's exact form. */}
                    <div>
                      <label style={{ display:"block", fontSize:12.5, fontWeight:600, color:"rgba(255,255,255,0.6)", marginBottom:8 }}>
                        Select Login Type
                      </label>
                      <div style={{ display:"flex", gap:10 }}>
                        {[
                          { key:"tracking", title:"Vehicle Tracking", Icon: TruckIcon },
                          { key:"fleet",    title:"Fleet Management", Icon: UserSquare2 },
                        ].map(opt => {
                          const active = loginProduct === opt.key;
                          return (
                            <button
                              type="button" key={opt.key}
                              onClick={() => { setLoginProduct(opt.key); setLoginActor("owner"); setGlobalError(""); }}
                              style={{
                                flex:1, display:"flex", alignItems:"center", gap:8, textAlign:"left", cursor:"pointer",
                                borderRadius:10, padding:"11px 12px",
                                background: active ? "rgba(99,102,241,0.15)" : "rgba(255,255,255,0.03)",
                                border: active ? "1.5px solid #6366F1" : "1.5px solid rgba(255,255,255,0.1)",
                                transition:"all 0.15s",
                              }}
                            >
                              <opt.Icon size={15} color={active ? "#C7D2FE" : "rgba(255,255,255,0.5)"} />
                              <span style={{ fontSize:13, fontWeight:700, color: active ? "#C7D2FE" : "#fff" }}>{opt.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Revealed only for Fleet Management — Owner vs Driver */}
                    <AnimatePresence>
                      {loginProduct === "fleet" && (
                        <motion.div
                          initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }} exit={{ opacity:0, height:0 }}
                          transition={{ duration:0.18 }} style={{ overflow:"hidden" }}
                        >
                          <label style={{ display:"block", fontSize:12.5, fontWeight:600, color:"rgba(255,255,255,0.6)", marginBottom:8 }}>
                            Login as
                          </label>
                          <div style={{ display:"flex", gap:10, marginBottom:2 }}>
                            {[
                              { key:"owner",  title:"Fleet Owner" },
                              { key:"driver", title:"Driver" },
                            ].map(opt => {
                              const active = loginActor === opt.key;
                              return (
                                <button
                                  type="button" key={opt.key}
                                  onClick={() => { setLoginActor(opt.key); setGlobalError(""); }}
                                  style={{
                                    flex:1, textAlign:"center", cursor:"pointer",
                                    borderRadius:10, padding:"10px 12px",
                                    background: active ? "rgba(99,102,241,0.15)" : "rgba(255,255,255,0.03)",
                                    border: active ? "1.5px solid #6366F1" : "1.5px solid rgba(255,255,255,0.1)",
                                  }}
                                >
                                  <span style={{ fontSize:13, fontWeight:700, color: active ? "#C7D2FE" : "#fff" }}>{opt.title}</span>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {loginProduct === "fleet" && loginActor === "driver" ? (
                      <>
                        <Field label="Email Address" type="email" required
                          value={driverForm.email} onChange={e => setD("email", e.target.value)}
                          placeholder="driver@company.com" error={driverErrors.email} />
                        <Field label="Password" type="password" required
                          value={driverForm.password} onChange={e => setD("password", e.target.value)}
                          placeholder="Enter your password" error={driverErrors.password} />
                        <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12.5, color:"rgba(255,255,255,0.6)", cursor:"pointer" }}>
                          <input type="checkbox" checked={driverForm.remember} onChange={e => setD("remember", e.target.checked)} />
                          Remember me
                        </label>
                      </>
                    ) : (
                      <>
                        <Field label="Email Address" type="email" required
                          value={loginForm.email} onChange={e => setL("email", e.target.value)}
                          placeholder="fleet@company.com" error={loginErrors.email} />
                        <div>
                          <Field label="Password" type="password" required
                            value={loginForm.password} onChange={e => setL("password", e.target.value)}
                            placeholder="Enter your password" error={loginErrors.password} />
                          <button type="button" onClick={openForgot} style={{
                            background:"none", border:"none", cursor:"pointer",
                            fontSize:12, color:"#818CF8", padding:"6px 0 0",
                            display:"block", textAlign:"right", width:"100%",
                            fontWeight:500,
                          }}>
                            Forgot password?
                          </button>
                        </div>
                      </>
                    )}

                    <button type="submit" disabled={loading} style={{
                      width:"100%", height:46, borderRadius:12, border:"none",
                      background: loading
                        ? "rgba(99,102,241,0.5)"
                        : "linear-gradient(135deg,#6366F1,#7C3AED)",
                      color:"#fff", fontSize:14, fontWeight:700,
                      cursor: loading ? "default" : "pointer",
                      display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                      boxShadow: loading ? "none" : "0 4px 20px rgba(99,102,241,0.45)",
                      transition:"all 0.2s",
                      marginTop:4,
                      animation: loading ? "none" : "glowPulse 2s infinite",
                    }}
                      onMouseEnter={e => { if (!loading) e.currentTarget.style.transform="translateY(-1px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; }}
                    >
                      {loading
                        ? <><Loader2 size={16} style={{ animation:"spinLoader 1s linear infinite" }} /> Signing in…</>
                        : "Sign In →"}
                    </button>

                    {loginProduct === "fleet" && loginActor === "driver" ? (
                      <p style={{ textAlign:"center", fontSize:12.5, color:"rgba(255,255,255,0.4)", margin:0, lineHeight:1.6 }}>
                        Driver accounts are created by your fleet owner — contact them for login access
                        or a password reset.
                      </p>
                    ) : (
                      <p style={{ textAlign:"center", fontSize:13, color:"rgba(255,255,255,0.4)", margin:0 }}>
                        No account?{" "}
                        <button type="button" onClick={() => setTab("register")} style={{
                          background:"none", border:"none", color:"#818CF8",
                          fontWeight:600, cursor:"pointer", fontSize:13, padding:0,
                        }}>
                          Create one free
                        </button>
                      </p>
                    )}
                  </motion.form>
                ) : (
                  <motion.form key="register"
                    initial={{ opacity:0, x:16 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:16 }}
                    transition={{ duration:0.2 }}
                    onSubmit={handleRegister} noValidate
                    style={{ display:"flex", flexDirection:"column", gap:14 }}
                  >
                    <div>
                      <label style={{ display:"block", fontSize:13, fontWeight:600, color:"rgba(255,255,255,0.75)", marginBottom:8 }}>
                        Choose your plan
                      </label>
                      <div style={{ display:"flex", gap:10 }}>
                        {[
                          { key:"tracking", title:"Vehicle Tracking", desc:"Live location, drivers, trips" },
                          { key:"fleet",    title:"Fleet Management", desc:"+ documents, maintenance, fuel, alerts" },
                        ].map(opt => {
                          const active = selectedTier === opt.key;
                          return (
                            <button
                              type="button"
                              key={opt.key}
                              onClick={() => setSelectedTier(opt.key)}
                              style={{
                                flex:1, textAlign:"left", cursor:"pointer",
                                borderRadius:10, padding:"12px 14px",
                                background: active ? "rgba(99,102,241,0.15)" : "rgba(255,255,255,0.03)",
                                border: active ? "1.5px solid #6366F1" : "1.5px solid rgba(255,255,255,0.1)",
                              }}
                            >
                              <div style={{ fontSize:13.5, fontWeight:700, color: active ? "#C7D2FE" : "#fff", marginBottom:2 }}>
                                {opt.title}
                              </div>
                              <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.45)" }}>
                                {opt.desc}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <Field label="Organization / Company Name" required
                      value={regForm.name} onChange={e => setR("name", e.target.value)}
                      placeholder="ACME Logistics Pvt Ltd" error={regErrors.name} />
                    <Field label="Email Address" type="email" required
                      value={regForm.email} onChange={e => setR("email", e.target.value)}
                      placeholder="admin@acmelogistics.com" error={regErrors.email} />

                    {/* Phone number */}
                    <div>
                      <label style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.7)", display:"block", marginBottom:5, letterSpacing:0.3 }}>
                        Phone Number<span style={{ color:"#F87171", marginLeft:2 }}>*</span>
                      </label>
                      <div style={{ display:"flex", gap:0 }}>
                        <div style={{
                          height:44, minWidth:52, borderRadius:"10px 0 0 10px",
                          background:"rgba(255,255,255,0.08)",
                          border:"1.5px solid rgba(255,255,255,0.12)",
                          borderRight:"none",
                          display:"flex", alignItems:"center", justifyContent:"center", gap:4,
                          flexShrink:0,
                        }}>
                          <Phone size={12} color="rgba(255,255,255,0.4)" />
                          <span style={{ fontSize:13, color:"rgba(255,255,255,0.5)", fontWeight:600 }}>+91</span>
                        </div>
                        <input
                          type="tel" maxLength={10}
                          value={regForm.phone}
                          onChange={e => setR("phone", e.target.value.replace(/\D/g, "").slice(0,10))}
                          placeholder="9876543210"
                          style={{
                            flex:1, height:44,
                            paddingLeft:12, paddingRight:12,
                            background:"rgba(255,255,255,0.06)",
                            border:`1.5px solid ${regErrors.phone ? "#F87171" : "rgba(255,255,255,0.12)"}`,
                            borderLeft:"none",
                            borderRadius:"0 10px 10px 0",
                            fontSize:14, color:"#fff", outline:"none",
                            letterSpacing:1,
                          }}
                        />
                      </div>
                      {regErrors.phone && <p style={{ fontSize:11, color:"#F87171", marginTop:4, margin:"4px 0 0" }}>{regErrors.phone}</p>}
                    </div>

                    <Field label="Billing Address (optional)"
                      value={regForm.address} onChange={e => setR("address", e.target.value)}
                      placeholder="Used on your invoices — street, city, state, PIN" />

                    <div style={{ display:"grid", gridTemplateColumns: narrow ? "1fr" : "1fr 1fr", gap:12 }}>
                      <Field label="GST Number (optional)"
                        value={regForm.gst} onChange={e => setR("gst", e.target.value.toUpperCase())}
                        placeholder="22AAAAA0000A1Z5" error={regErrors.gst} />
                      <Field label="PAN Number (optional)"
                        value={regForm.pan} onChange={e => setR("pan", e.target.value.toUpperCase())}
                        placeholder="AAAAA0000A" error={regErrors.pan} />
                    </div>

                    <div style={{ display:"grid", gridTemplateColumns: narrow ? "1fr" : "1fr 1fr", gap:12 }}>
                      <Field label="Password" type="password" required
                        value={regForm.password} onChange={e => setR("password", e.target.value)}
                        placeholder="Min 6 chars" error={regErrors.password} />
                      <Field label="Confirm" type="password" required
                        value={regForm.confirm} onChange={e => setR("confirm", e.target.value)}
                        placeholder="Repeat" error={regErrors.confirm} />
                    </div>

                    {/* Coupon */}
                    <div style={{ position:"relative" }}>
                      <Tag size={13} style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:"rgba(255,255,255,0.3)" }} />
                      <input type="text" placeholder="Coupon code (optional)"
                        value={regForm.coupon} onChange={e => setR("coupon", e.target.value)}
                        style={{
                          width:"100%", height:42, borderRadius:10, fontSize:13,
                          border:"1.5px solid rgba(255,255,255,0.1)", outline:"none",
                          paddingLeft:34, paddingRight:12, boxSizing:"border-box",
                          color:"#fff", background:"rgba(255,255,255,0.06)",
                          textTransform:"uppercase", letterSpacing: regForm.coupon ? "0.08em" : 0,
                        }}
                      />
                    </div>

                    <button type="submit" disabled={loading} style={{
                      width:"100%", height:46, borderRadius:12, border:"none",
                      background: loading
                        ? "rgba(99,102,241,0.5)"
                        : "linear-gradient(135deg,#6366F1,#7C3AED)",
                      color:"#fff", fontSize:14, fontWeight:700,
                      cursor: loading ? "default" : "pointer",
                      display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                      boxShadow: loading ? "none" : "0 4px 20px rgba(99,102,241,0.45)",
                      transition:"all 0.2s", marginTop:4,
                    }}
                      onMouseEnter={e => { if (!loading) e.currentTarget.style.transform="translateY(-1px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; }}
                    >
                      {loading
                        ? <><Loader2 size={16} style={{ animation:"spinLoader 1s linear infinite" }} /> Creating account…</>
                        : "Create Account →"}
                    </button>

                    <p style={{ fontSize:11, color:"rgba(255,255,255,0.3)", textAlign:"center", margin:0, lineHeight:1.5 }}>
                      Registration is free. Payment required to start tracking.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>}

              {/* Features strip — hide in forgot mode */}
              {!forgotMode && <div style={{
                marginTop:20, paddingTop:20,
                borderTop:"1px solid rgba(255,255,255,0.07)",
                display:"flex", flexDirection:"column", gap:8,
              }}>
                {[
                  "Live location from FASTag toll data",
                  "Smart auto-refresh every 2–4 hours",
                  "2 free calls on signup · plans from ₹300",
                ].map(f => (
                  <div key={f} style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{
                      width:16, height:16, borderRadius:5,
                      background:"rgba(99,102,241,0.2)",
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                    }}>
                      <Check size={9} color="#818CF8" strokeWidth={2.5} />
                    </div>
                    <span style={{ fontSize:11.5, color:"rgba(255,255,255,0.4)" }}>{f}</span>
                  </div>
                ))}
              </div>}

              {/* Spacer in forgot mode so the card doesn't feel too tight */}
              {forgotMode && <div style={{ height:8 }} />}
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
